import store from 'store';
import {
  AuthError,
  AuthenticationResult,
  EventType,
  IPublicClientApplication,
} from '@azure/msal-browser';
import { getMsalInstance } from '@kf/ic2fx';
import * as msTeams from '@microsoft/teams-js';
import { storagePersistedKey } from './constants';
import { ErrorMessage, NavigationPath } from 'models/enum';

// Extend the Window interface to include microsoftTeams
declare global {
  interface Window {
    microsoftTeams?: typeof msTeams;
    nativeInterface?: any;
  }
}


import {
  calculateTokenRemainingTime,
  getDecodedB2cToken,
  isExpired,
} from 'modules/auth/components/b2c/utils';
import { IAuthTokenData } from 'modules/auth/models/IAuth.interface';
import { loginUserSuccess } from 'modules/auth/stateManagement/actionsCreator';
import { CustomLoginError, loginRequest } from '@/auth/msalConfig';

export const checkAppIsRunningInTeams = () => {
  const microsoftTeamsLib = msTeams || window.microsoftTeams;
  const isTeamsApp = new URLSearchParams(window.location.search).get('isTA');

  if (!microsoftTeamsLib) return false;

  if (
    (window.parent === window.self && (window as any).nativeInterface) ||
    window.name === 'embedded-page-container' ||
    window.name === 'extension-tab-frame'
  ) {
    return true;
  }

  if (microsoftTeamsLib) {
    if (isTeamsApp === 'true') {
      sessionStorage.setItem('isTA', 'true');
      return true;
    }
    if (sessionStorage.getItem('isTA') === 'true') return true;
  }

  return false;
};

export const isAppRunningInTeams = checkAppIsRunningInTeams();

export const getTokenFromLocalStorage = () => {
  const token = localStorage.getItem(storagePersistedKey.MSALIDToken);

  if (!token) return;

  const decodedB2cToken = getDecodedB2cToken(token);

  if (isExpired(decodedB2cToken?.exp)) {
    localStorage.removeItem(storagePersistedKey.MSALIDToken);
    return;
  }

  return token;
};

export const isInPopupRoute =
  window.location.pathname === NavigationPath.authstart ||
  window.location.pathname === NavigationPath.authend;

export const getAuthErrorMessage = (reason?: string) => {
  switch (reason) {
    case CustomLoginError.canceledByUser:
      return ErrorMessage.userCancelled;
    case EventType.LOGIN_FAILURE:
      return ErrorMessage.loginFailureError;
    case CustomLoginError.executiveUserNotAllowed:
      return ErrorMessage.executiveUserError;
    case CustomLoginError.commonErrorMessage:
      return ErrorMessage.commonError;
    default:
      return ErrorMessage.defaultError;
  }
};

export const clearMsalSession = (msalInstance: IPublicClientApplication) => {
  localStorage.clear();
  sessionStorage.clear();
  msalInstance.logout();
  window.location.reload();
};

export const onGetAuthTokenSuccess = (
  authResult: AuthenticationResult,
  msalInstance: IPublicClientApplication,
  checkIsExecutive: boolean = true,
  checkIsExpired: boolean = true
) => {
  const b2cToken = authResult?.idToken;
  const account = authResult?.account;
  const decodedB2cToken = getDecodedB2cToken(b2cToken);

  const tokenData: IAuthTokenData = {
    is_ic2_executive: checkIsExecutive && decodedB2cToken.is_ic2_executive,
    token: b2cToken,
    isExpired: checkIsExpired && isExpired(decodedB2cToken?.exp),
  };

  if (tokenData.isExpired) clearMsalSession(msalInstance);

  if (tokenData.is_ic2_executive) return tokenData;

  localStorage.setItem(storagePersistedKey.MSALIDToken, b2cToken);
  msalInstance.setActiveAccount(account);
  return tokenData;
};

export const onGetAuthTokenError = (err: any, instance: any) => {
  const error = err as AuthError;
  if (
    error.name === 'BrowserAuthError' ||
    error.name === 'InteractionRequiredAuthError'
  ) {
    clearMsalSession(instance);
  }
};

export const fetchAccessTokenSilently = async (
  instance: IPublicClientApplication,
  checkIsStateAllowed: boolean = false
): Promise<any> => {
  await instance
    .acquireTokenSilent(loginRequest)
    .then((response: AuthenticationResult) => {
      const authTokenData = onGetAuthTokenSuccess(response, instance);
      console.log("fetchAccessTokenSilently Silent token acquisition succeeded", response);
      if (!checkIsStateAllowed) {
        if (authTokenData.is_ic2_executive) window.location.reload();
        store.dispatch(loginUserSuccess(response?.idToken, []));
        return authTokenData.token;
      }

      console.log("fetchAccessTokenSilently Token data", authTokenData);

      return authTokenData;
    })
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    .catch((err: any) => onGetAuthTokenError(err, instance));
};

export const acquireAccessToken = (
  checkForMultipleAccounts: boolean = false,
  fetchTokenSilently: boolean = false
) => {
  const idToken = store.getState().auth.ssoToken;
  const decodedB2cToken = getDecodedB2cToken(idToken);
  let instance = getMsalInstance();
  if (
    checkForMultipleAccounts &&
    decodedB2cToken?.client_email &&
    decodedB2cToken?.client_email !==
      instance.getActiveAccount()?.idTokenClaims?.client_email
  ) {
    const presentAccountInfo = instance
      .getAllAccounts()
      .find(
        (account) =>
          account.idTokenClaims?.client_email === decodedB2cToken?.client_email
      );

    if (presentAccountInfo) instance.setActiveAccount(presentAccountInfo);
    else window.location.reload();
  }
  if (isExpired(decodedB2cToken?.exp) || fetchTokenSilently) {
    return fetchAccessTokenSilently(instance);
  } else {
    return idToken;
  }
};

export const isTokenExpired = (): boolean => {
  const idToken = store.getState().auth.ssoToken;
  const decodedB2cToken = getDecodedB2cToken(idToken);

  return isExpired(decodedB2cToken?.exp);
};

export const getTokenRemainingTime = (): number => {
  const idToken = store.getState().auth.ssoToken;
  const decodedB2cToken = getDecodedB2cToken(idToken);

  return calculateTokenRemainingTime(decodedB2cToken?.exp);
};

export const checkForInitialWindowSize = (value: number[]): boolean => {
  return value.every((item: number) => item === 0);
};
